/**
 * Quem decide QUAL modelo o chat usa — a partir da lista do backend, nunca de
 * um literal no front.
 *
 * ★ Por que este arquivo existe (Q7-A08, 03/09/2026): o store nascia com
 * `'claude-sonnet-4-6'` cravado em quatro lugares. Esse id SAIU de
 * `GET /api/v1/intelligence-v2/supported-models` (a lista hoje é
 * `claude-sonnet-5`, `claude-haiku-4-5-20251001`, `kimi-k2.6`,
 * `deepseek-v4-pro`, `deepseek-v4-flash`), e quem tinha o valor antigo em
 * `localStorage.iaCrmSelectedModel` mandava TODO turno com ele — o backend
 * respondia 400 e o chat simplesmente não funcionava, sem nada na tela
 * explicando por quê.
 *
 * Literal de id de modelo no front tem prazo de validade e ninguém avisa
 * quando ele vence. A lista do backend é a única fonte.
 *
 * Shape do `value` da rota:
 *
 *   { default: 'claude-sonnet-5',
 *     models: [{ id, label, provider, is_default, description }, ...] }
 *
 * Os dois canais de "quem é o padrão" (`default` no topo e `is_default` por
 * item) são lidos NESTA ordem, e ambos precisam existir NA LISTA — um
 * `default` que aponta para fora dela é exatamente o defeito que estamos
 * corrigindo, um nível acima.
 */

/**
 * Ausência de modelo escolhido. String vazia e não `null` porque o consumidor
 * faz `selectedModel || null` antes de mandar no corpo do turno: sem id, o
 * backend aplica o padrão DELE, que é o desfecho correto enquanto a lista não
 * chegou.
 */
export const ID_SEM_MODELO = ''

/**
 * Os ids da lista, sem entrada malformada.
 *
 * @param {Object} payload `value` de `/supported-models`
 * @returns {Array<String>}
 */
export const idsDosModelos = (payload) => {
  const models = (payload && payload.models) || []
  return models
    .filter((m) => m && typeof m.id === 'string' && m.id)
    .map((m) => m.id)
}

/**
 * O modelo padrão, na ordem: `default` do payload (se estiver na lista) →
 * primeiro item marcado `is_default` → primeiro item da lista → vazio.
 *
 * @param {Object} payload `value` de `/supported-models`
 * @returns {String} id do modelo, ou `ID_SEM_MODELO`
 */
export const modeloPadrao = (payload) => {
  const ids = idsDosModelos(payload)
  if (!ids.length) return ID_SEM_MODELO

  const declarado = payload && payload.default
  if (declarado && ids.indexOf(declarado) !== -1) return declarado

  const models = (payload && payload.models) || []
  const marcado = models.filter((m) => m && m.is_default && m.id)[0]
  if (marcado) return marcado.id

  return ids[0]
}

/**
 * Reconcilia o modelo ESCOLHIDO (vindo do `localStorage`) com a lista viva.
 *
 * ⚠️ Lista VAZIA significa que a rota falhou, não que não há modelo: aí a
 * escolha da pessoa é preservada, porque descartá-la por causa de uma queda de
 * rede seria trocar um defeito por outro.
 *
 * @param {String} atual id salvo/escolhido
 * @param {Object} payload `value` de `/supported-models`
 * @returns {String} id válido, ou `ID_SEM_MODELO`
 */
export const modeloValidoOuPadrao = (atual, payload) => {
  const ids = idsDosModelos(payload)
  if (!ids.length) return atual || ID_SEM_MODELO
  if (atual && ids.indexOf(atual) !== -1) return atual
  return modeloPadrao(payload)
}
